import { GrowthBook, GrowthBookProvider } from '@growthbook/growthbook-react';
import { useQueryClient } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { ReactNode, useEffect, useState } from 'react';
import { useSession } from '@/auth/index';
import { CookiesStorage } from '@/storage/cookies';
import { useScreenSize } from '@/utils/index';

interface GBWrapperProps {
  children: ReactNode;
  enableDevMode: boolean;
  apiHost: string;
  clientKey: string;
  decryptionKey: string;
  subscribeToChanges: boolean;
  customAttributes: Record<string, unknown>;
  setIsGbReady?: (isReady: boolean) => void;
  brandName: string;
}

export function GrowthbookWrapper({
  children,
  enableDevMode,
  apiHost,
  clientKey,
  decryptionKey,
  subscribeToChanges,
  customAttributes,
  setIsGbReady,
  brandName,
}: GBWrapperProps) {
  const [growthbook, setGrowthbook] = useState<GrowthBook | null>(null);
  const {
    session: { account },
  } = useSession();
  const { isMobile } = useScreenSize();
  const deviceType = isMobile ? 'web-mobile' : 'desktop';
  const queryClient = useQueryClient();

  useEffect(() => {
    loadGrowthbook();
  }, []);

  async function loadGrowthbook() {
    const growthbook = new GrowthBook({
      apiHost: apiHost,
      clientKey: clientKey,
      decryptionKey: decryptionKey,
      enableDevMode: enableDevMode,
      subscribeToChanges: subscribeToChanges,
      trackingCallback: async (experiment, result) => {
        const coreAssignation = {
          experiment_key: experiment.key,
          variation_key: result.key,
          hash_attribute: result.hashAttribute,
          hash_value: result.hashValue,
        };
        if (!assignationExists(experiment.key, coreAssignation.variation_key)) {
          const response = await createAssignationsOnCore(coreAssignation);
          if (response.ok) {
            queryClient.invalidateQueries(['account']);
          }
        }
      },
    });

    await growthbook.loadFeatures();
    setGrowthbook(growthbook);
  }

  function assignationExists(experimentKey: string, variationKey: string) {
    return account?.account.active_ab_tests.tests.some(
      (test) =>
        test.experiment_key === experimentKey &&
        test.variation_key === variationKey
    );
  }

  function createAssignationsOnCore(assignation: {
    experiment_key: string;
    variation_key: string;
  }) {
    return fetch('/api/v5/ab_test', {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
      },
      body: JSON.stringify({
        experiments: [assignation],
      }),
    });
  }

  function getSessionCookie() {
    const storage = new CookiesStorage();
    const bv_sess_key = 'bv_sess';
    const bv_sess_value = storage.getValue(bv_sess_key);
    return bv_sess_value;
  }

  useEffect(() => {
    if (account?.account.user_info.user_code && growthbook?.ready) {
      const joinDate = account.account.user_info.join_date;
      const defaultAttributes = {
        user_code: account.account.user_info.user_code,
        // this is going to be addressed in a tech debt ticket to get the user_id from the user_info on the account response
        user_id: account.account.active_ab_tests.tests[0]
          ? account.account.active_ab_tests.tests[0].user_id
          : null,
        brand: (brandName || '').toLowerCase(),
        email: account?.account.user_info.email,
        signup_timestamp: joinDate ? new Date(joinDate).valueOf() : null,
        device_type: deviceType,
        signup_date: joinDate
          ? DateTime.fromJSDate(new Date(joinDate)).toFormat(
              'yyyy-MM-dd HH:mm:ss'
            )
          : null,
        bv_sess: getSessionCookie(),
        signup_type: account?.account.subscription_info.user_signup_flow,
      };
      const attributes = { ...defaultAttributes, ...customAttributes };

      growthbook.setAttributes(attributes);
      setIsGbReady && setIsGbReady(true);
    }
  }, [account, growthbook, deviceType, customAttributes]);

  if (!growthbook) return null;
  return (
    <GrowthBookProvider growthbook={growthbook}>{children}</GrowthBookProvider>
  );
}
